import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { ChangeEvent, SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CREATE_USER, EMAIL_REGEX, UPDATE_USER } from '../../constants';
import { IdName } from '../../models/global';
import { State } from '../../models/state';
import { getOrganizations } from '../../store/actions/organization';
import { getStaticValues, getSurveySeries } from '../../store/actions/survey';
import { createUser, getUserById, updateUser } from '../../store/actions/user';
import Button from '../FormComponents/Button';
import Dropdown from '../FormComponents/Dropdown';
import MultiSelect from '../FormComponents/MultiSelect';
import Switch from '../FormComponents/Switch';
import TextField from '../FormComponents/TextField';
import NoData from '../NoData/NoData';
import ToastMessage from '../ToastMessage/ToastMessage';

interface UserFormProps {
  type: string;
}

function UserForm(props: UserFormProps) {
  const { type } = props;
  const { userId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    language: '',
    role: 'Member',
    position: '',
    active: true,
    organizationId: null,
  });
  const [sameLanguageAsOrg, setSameLanguageAsOrg] = useState(false);
  const [formClicked, setFormClicked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [toast, setToast] = useState('');
  const [notFound, setNotFound] = useState(false);
  const { seriesList, languages } = useSelector((state: State) => state.survey);
  const { organizations } = useSelector((state: State) => state.organization);
  const [emailValid, setEmailValid] = useState(true);
  const [surveySeriesNames, setSurveySeriesNames] = useState<string[]>([]);
  const requiredFields = ['name', 'email', 'role', 'organizationId'];
  const requiredFieldsAdmin = ['name', 'email', 'role'];

  const searchParams = new URLSearchParams(location.search);
  const organizationIdFromQuery = searchParams.get('organizationId');

  const getLanguagesAndRoles = () => {
    dispatch(getStaticValues());
  };

  const getOrganizationList = () => {
    dispatch(getOrganizations({ limit: 1000, page: 1 }));
  };

  const getSurveySeriesList = () => {
    dispatch(
      getSurveySeries(
        {
          limit: 100,
          page: 1,
        },
        () => {},
      ),
    );
  };

  const getData = () => {
    dispatch(
      getUserById(userId, res => {
        if (!res) {
          setNotFound(true);
        } else {
          if (res.organization) {
            setSameLanguageAsOrg(!res.language);
          }
          setFormData(res);
          setSurveySeriesNames(res.surveySeries?.map(surveySeries => surveySeries.name) || []);
        }
      }),
    );
  };

  useEffect(() => {
    if (organizationIdFromQuery) {
      handleOrganization(organizationIdFromQuery);
    }
    getLanguagesAndRoles();
    getOrganizationList();
    getSurveySeriesList();
    if (type === UPDATE_USER) {
      if (location?.state?.message) {
        setToastType('success');
        setToast(location?.state?.message);
        window.history.replaceState({}, '');
      }
      getData();
    }
  }, [setFormData, setToastType, setToast]);

  const checkRequiredFields = () => {
    if (formData.role === 'Admin') {
      return requiredFieldsAdmin.every((key: string) => formData[key]);
    }
    return requiredFields.every((key: string) => formData[key]);
  };

  const handleFormClick = () => {
    setFormClicked(true);
    if (formData.role === 'Member' && !organizations?.length) {
      setToastType('error');
      setToast('Please add an organization first to be able to associate a user to it');
      return;
    }
    if (!checkRequiredFields()) {
      setToastType('error');
      setToast('Please enter all mandatory fields');
      return;
    }

    if (!EMAIL_REGEX.test(formData.email)) {
      setEmailValid(false);
      setToastType('error');
      setToast('Please enter valid email');
      return;
    }
    setEmailValid(true);
    setLoading(true);
    const data = { ...formData } as typeof formData & { surveySeriesIds: string[] };
    data.surveySeriesIds = seriesList.filter(item => surveySeriesNames.includes(item.name)).map(el => el.id);
    if (type === CREATE_USER) {
      dispatch(
        createUser(data, (res, message) => {
          setLoading(false);
          if (res) {
            const navigateTo = organizationIdFromQuery ? `/organizations/${organizationIdFromQuery}` : '/users';
            navigate(navigateTo, {
              state: {
                message: 'User is created.',
              },
            });
          } else {
            setToastType('error');
            setToast(message || 'Opps, unknown error occured.');
          }
        }),
      );
    } else {
      dispatch(
        updateUser({ id: userId, ...data }, (res, message) => {
          setLoading(false);
          if (res) {
            navigate('/users', {
              state: {
                message: 'User is updated.',
              },
            });
          } else {
            setToastType('error');
            setToast(message || 'Opps, unknown error occured.');
          }
        }),
      );
    }
  };

  const handleSameLanguage = (checked: boolean) => {
    setSameLanguageAsOrg(checked);
    if (checked) {
      setFormData({ ...formData, language: '' });
    }
  };

  const handleLanguage = (lang: string) => {
    setFormData({ ...formData, language: lang });
    if (!lang && formData.organizationId) {
      setSameLanguageAsOrg(true);
    }
  };

  const handleOrganization = orgId => {
    setFormData({ ...formData, organizationId: orgId || null });
    if (!orgId) {
      setSameLanguageAsOrg(false);
    } else if (!formData.language) {
      setSameLanguageAsOrg(true);
    }
  };

  const handleRole = (role: string) => {
    const newData = { ...formData, role };
    if (role === 'Admin') {
      newData.organizationId = null;
      newData.language = '';
      setSameLanguageAsOrg(false);
    }
    setFormData(newData);
  };

  const handleSurveySeries = (value: string) => {
    const exists = surveySeriesNames.includes(value);
    if (exists) {
      setSurveySeriesNames(surveySeriesNames.filter(name => name !== value));
    } else {
      setSurveySeriesNames([...surveySeriesNames, value]);
    }
  };

  const organizationOptions = organizations.map(org => ({
    ...org,
    name: org.country?.name ? `${org.name} (${org.country.name})` : org.name,
  }));

  if (type === UPDATE_USER && notFound) {
    return <NoData message='No user found' />;
  }

  return (
    <>
      <Box>
        <Typography variant='h5'>User {type === CREATE_USER ? 'creation' : 'details'}</Typography>
        <Grid container marginTop={2} gap={4}>
          <Grid item xs={5}>
            <TextField
              label='Name'
              id='name'
              variant='outlined'
              style={{ width: '100%' }}
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              required={requiredFields.includes('name')}
              error={formClicked && requiredFields.includes('name') && !formData.name}
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              label='Email'
              id='email'
              variant='outlined'
              style={{ width: '100%' }}
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
              required={requiredFields.includes('email')}
              error={formClicked && requiredFields.includes('email') && (!formData.email || !emailValid)}
            />
          </Grid>
        </Grid>
        <Grid container marginTop={2} gap={4}>
          <Grid item xs={5}>
            <TextField
              label='Position'
              id='position'
              variant='outlined'
              style={{ width: '100%' }}
              value={formData.position}
              onChange={e => setFormData({ ...formData, position: e.target.value })}
            />
          </Grid>
          {formData.role !== 'Admin' && (
            <Grid item xs={5}>
              <Dropdown
                value={formData.language}
                label='Language'
                style={{ width: '100%' }}
                options={languages}
                onChange={(_: SyntheticEvent, value: string) => handleLanguage(value)}
                disabled={sameLanguageAsOrg}
              />
            </Grid>
          )}
        </Grid>

        <Grid container marginTop={2} gap={4}>
          {formData.role !== 'Admin' ? (
            <>
              <Grid item xs={5}>
                <Dropdown
                  value={organizationOptions.find(el => el.id === formData.organizationId)?.name || ''}
                  label='Organization'
                  style={{ width: '100%' }}
                  options={organizationOptions}
                  onChange={(_: SyntheticEvent, value: IdName) => handleOrganization(value?.id)}
                  required={requiredFields.includes('organizationId')}
                  error={formClicked && requiredFields.includes('organizationId') && !formData.organizationId}
                  disabled={!!organizationIdFromQuery}
                />
              </Grid>
              <Grid item xs={4}>
                <Switch
                  label={`Same language as organization's ${
                    formData.organizationId
                      ? `(${organizations.find(el => el.id === formData.organizationId)?.language})`
                      : ''
                  }`}
                  checked={!!sameLanguageAsOrg}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleSameLanguage(e.target.checked)}
                  disabled={!formData.organizationId}
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={5} mb={5}></Grid>
          )}
        </Grid>

        <Grid container marginTop={2} gap={4}>
          {!!formData.organizationId && (
            <Grid item xs={1} alignContent='center'>
              <Box display='flex' gap={1} alignItems='center'>
                <Typography variant='subtitle1'>Country:</Typography>
                <Typography variant='body2'>
                  {organizations.find(el => el.id === formData.organizationId)?.country?.name || 'N/A'}
                </Typography>
              </Box>
            </Grid>
          )}
          <Grid item xs={1}>
            <Switch
              label='Admin'
              checked={formData.role === 'Admin'}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleRole(e.target.checked ? 'Admin' : 'Member')}
            />
          </Grid>
          <Grid item xs={1}>
            <Switch
              label='Active'
              checked={formData.active}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, active: e.target.checked })}
            />
          </Grid>
        </Grid>

        <Grid container marginTop={2} gap={4}>
          {formData.role !== 'Admin' && (
            <>
              <Grid item xs={5}>
                <MultiSelect
                  value={surveySeriesNames}
                  style={{ width: '100%' }}
                  label='Survey Series'
                  options={seriesList}
                  onChange={handleSurveySeries}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Box className='submit-button'>
          <Button text={type === CREATE_USER ? 'Create' : 'Update'} onClick={handleFormClick} loading={loading} />
        </Box>
      </Box>
      <ToastMessage toast={toast} close={() => setToast('')} messageType={toastType} />
    </>
  );
}

export default UserForm;
